/**
 * DataRetention Component
 *
 * Renders the Data Retention Policy page for RILE Connect. Describes the
 * types of data collected, retention periods (displayed in a table), account
 * deletion procedures, organization data handling, third-party services,
 * data security practices, and policy change notifications.
 *
 * Displayed as a nested route within the MainPolicies layout at
 * /policies/data-retention.
 */
import React from 'react';

import SEO from '@/components/common/SEO';

export default function DataRetention() {
    return (
        <>
            <SEO
                title="Data Retention Policy"
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Policies', path: '/policies' },
                    {
                        name: 'Data Retention',
                        path: '/policies/data-retention',
                    },
                ]}
            />
            <div className="data-retention-policy">
                {/* Policy header and last-updated date */}
                <h2 className="text-primary mb-4">Data Retention Policy</h2>
                <p className="text-muted mb-4">
                    <em>Last Updated: February 2026</em>
                </p>

                {/* Policy sections: data collected, retention periods,
                    account deletion, org data, third-party services,
                    security, changes, and contact */}
                <p>
                    RILE Connect is committed to minimal data collection and
                    transparent governance. This policy explains what data we
                    store, how long we keep it, and how you can request its
                    removal.
                </p>

                <h5 className="mt-4 mb-3 fw-bold">Data We Collect</h5>
                <p>
                    We collect only the information necessary to operate the
                    platform:
                </p>
                <ul>
                    <li>
                        <strong>Account information</strong>: Name, email
                        address, and profile photo you provide during
                        registration
                    </li>
                    <li>
                        <strong>Profile data</strong>: Bio, mentor profile
                        details, and organization affiliations you choose to add
                    </li>
                    <li>
                        <strong>Organization data</strong>: Information
                        submitted during organization onboarding, including
                        descriptions, sectors, regions, and contact details
                    </li>
                    <li>
                        <strong>Usage data</strong>: Anonymous analytics to
                        understand how the platform is used and identify gaps in
                        the ecosystem
                    </li>
                    <li>
                        <strong>AI assistant conversations</strong>: Chat
                        messages exchanged with the AI assistant to provide
                        conversation history and improve the service
                    </li>
                </ul>

                {/* Retention periods table: shows each data type and how long it is kept */}
                <h5 className="mt-4 mb-3 fw-bold">Retention Periods</h5>
                <table className="table table-bordered">
                    <thead className="table-light">
                        <tr>
                            <th>Data Type</th>
                            <th>Retention Period</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Account and profile data</td>
                            <td>
                                Retained while your account is active, deleted
                                within 30 days of account deletion request
                            </td>
                        </tr>
                        <tr>
                            <td>Organization profiles</td>
                            <td>
                                Retained while the organization is active in the
                                ecosystem directory
                            </td>
                        </tr>
                        <tr>
                            <td>AI assistant conversations</td>
                            <td>
                                Retained for 90 days, then automatically deleted
                            </td>
                        </tr>
                        <tr>
                            <td>Anonymous analytics</td>
                            <td>Retained in aggregate form indefinitely</td>
                        </tr>
                        <tr>
                            <td>Authentication tokens</td>
                            <td>
                                Managed by Firebase; expire per session policy
                            </td>
                        </tr>
                    </tbody>
                </table>

                <h5 className="mt-4 mb-3 fw-bold">Account Deletion</h5>
                <p>
                    You may request deletion of your account and all associated
                    personal data at any time by contacting us at{' '}
                    <a href="mailto:contact@rileconnect.org">
                        contact@rileconnect.org
                    </a>
                    . Upon receiving your request, we will:
                </p>
                <ul>
                    <li>
                        Remove your personal profile data (name, bio, photo,
                        mentor profile, affiliations) within 30 days
                    </li>
                    <li>
                        Remove your authentication credentials from Firebase
                    </li>
                    <li>Delete your AI assistant conversation history</li>
                    <li>
                        Remove your role assignments from any organizations you
                        manage
                    </li>
                </ul>

                <h5 className="mt-4 mb-3 fw-bold">Organization Data</h5>
                <p>
                    Organization profiles in the ecosystem directory are
                    considered public ecosystem data. If you are the owner of an
                    organization and wish to remove it from the directory:
                </p>
                <ul>
                    <li>
                        Contact us at{' '}
                        <a href="mailto:contact@rileconnect.org">
                            contact@rileconnect.org
                        </a>{' '}
                        with your removal request
                    </li>
                    <li>
                        The organization profile and its relationships will be
                        removed within 30 days
                    </li>
                    <li>
                        Anonymous aggregate statistics (e.g., sector counts) may
                        be retained
                    </li>
                </ul>

                <h5 className="mt-4 mb-3 fw-bold">Third-Party Services</h5>
                <p>
                    RILE Connect uses the following third-party services that
                    may process your data under their own retention policies:
                </p>
                <ul>
                    <li>
                        <strong>Firebase (Google)</strong>: Authentication and
                        identity verification
                    </li>
                    <li>
                        <strong>Amazon Web Services</strong>: Infrastructure
                        hosting and file storage
                    </li>
                    <li>
                        <strong>Google Analytics</strong>: Anonymous usage
                        analytics
                    </li>
                </ul>
                <p>
                    We encourage you to review these services&apos; privacy
                    policies for details on their data handling practices.
                </p>

                <h5 className="mt-4 mb-3 fw-bold">Data Security</h5>
                <p>
                    All data is encrypted in transit (TLS) and at rest. Access
                    to production databases is restricted to authorized
                    personnel. We follow the principle of least privilege for
                    all system access.
                </p>

                <h5 className="mt-4 mb-3 fw-bold">Changes to This Policy</h5>
                <p>
                    We may update this policy as the platform evolves. Material
                    changes will be communicated through the platform. Continued
                    use of RILE Connect after changes constitutes acceptance of
                    the updated policy.
                </p>

                <h5 className="mt-4 mb-3 fw-bold">Contact</h5>
                <p>
                    Questions about data retention or deletion requests? Contact
                    us at{' '}
                    <a href="mailto:contact@rileconnect.org">
                        contact@rileconnect.org
                    </a>
                </p>
            </div>
        </>
    );
}
